// The interaction core behind both threshold sliders (ACT2-534) — drag, keyboard, clamping, snapping,
// and the handle model. Extracted from the two near-identical copies that had already drifted apart:
// the measured histogram floored its span and step at 1 (unreachable track and integer-only steps on a
// sub-unit column) long after the approximated slider had fixed both.
//
// It owns the VALUE domain only. Nothing here knows whether the plot above it is drawn as bars, bands,
// or (later) a curve — that stays with the caller, which is what makes the shape swappable.

import type { PointerEvent, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { ConditionValue } from '../../types';
import type { AxisScaleKind } from '../lib/axis-scale';
import { axisScale } from '../lib/axis-scale';
import type { DistributionDomain } from '../lib/distribution-domains';
import type { Span } from '../lib/slider-ops';
import { continuousSegments, RANGE_OPS } from '../lib/slider-ops';
import { defaultDragStep, snapToStep } from '../lib/step';

export interface SliderHandle {
  pos: number; // axis percent
  value: number;
  label: string;
  ariaLabel: string;
  onKey: (v: number) => void;
}

export interface ThresholdSlider {
  wrapRef: RefObject<HTMLDivElement | null>;
  min: number;
  max: number;
  step: number;
  /** The row's operator mapped onto the engine's numeric vocabulary (gt/gte/lt/lte/eq/neq/btw/nbtw). */
  op: string;
  isRange: boolean;
  /** Whether the predicate actually holds a value — a cleared input greys the handle at a parked default. */
  committed: boolean;
  single: number;
  lo: number;
  hi: number;
  /** The committed value(s) exactly as stored — UNCLAMPED, so `null` for unset and possibly outside the
   *  plotted frame. Anything asking "which bucket holds this value" must use this: the clamped `single`
   *  above always lands inside the chart, which turns "outside the charted range" into a false match on
   *  an edge bucket. */
  raw: { single: number | null; lo: number | null; hi: number | null };
  pct: (v: number) => number;
  /** The arrow-key step AT a value — constant on a linear axis, magnitude-relative on a log one. */
  stepAt: (v: number) => number;
  clampValue: (v: number) => number;
  handles: SliderHandle[];
  /** Definite-match span(s) for a continuous operator; empty for eq/neq (see `continuousSegments`). */
  segments: Span[];
  disabled: boolean;
  pointerProps: {
    onPointerDown: (e: PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (e: PointerEvent<HTMLDivElement>) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
  };
}

export function useThresholdSlider({
  min,
  max,
  step,
  scale = 'linear',
  integerValued,
  fallback,
  domain,
  operator,
  value,
  onPatch,
  ariaLabel,
  disabled = false,
}: {
  min: number;
  max: number;
  /** Caller-supplied drag step; absent ⇒ the nice-number default derived from the span. */
  step?: number;
  /** Which space the axis is linear in. MUST match how the bins were binned — see `axis-scale.ts`. */
  scale?: AxisScaleKind;
  /** Whether the plotted values are whole numbers — floors the derived step at 1 for an integer column. */
  integerValued: boolean;
  /** Where an UNSET handle parks (median / quartiles) so a cleared input still reads as a position. */
  /**
   * Where an UNSET handle parks. `single` is optional: absent, it is the midpoint OF THE AXIS, which is
   * the only midpoint that means "the middle of the chart" on a non-linear one. Callers used to pass
   * `(min + max) / 2` — a midpoint by VALUE — and on a log frame of 100..500,000 that number sits at
   * 92% of the track, so an untouched rule opened looking as if it already excluded almost everyone.
   * `lo`/`hi` take `min`/`max` and are unaffected: those are frame ENDS, not midpoints.
   */
  fallback: { single?: number; lo: number; hi: number };
  domain: DistributionDomain;
  operator: string;
  value: ConditionValue;
  onPatch: (patch: ConditionValue) => void;
  ariaLabel: string;
  disabled?: boolean;
}): ThresholdSlider {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<0 | 1 | null>(null);
  // Cached at pointer-down and reused for every move: the plot cannot resize mid-drag, so re-reading
  // layout (getBoundingClientRect forces synchronous layout) on each pointermove is wasted work.
  const dragRect = useRef<DOMRect | null>(null);
  // Pointer-drag commits are coalesced to one per animation frame (see onPointerMove).
  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef<(() => void) | null>(null);
  // An operator change REMOUNTS this control (the panel keys on shape+operator) and the encoding guard can
  // unmount it outright, either of which can happen mid-drag. An orphaned frame would still fire, writing
  // a value derived from the old domain's writer into whatever replaced it.
  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const flushDrag = () => {
    rafRef.current = null;
    const fn = pendingRef.current;
    pendingRef.current = null;
    fn?.();
  };

  // NEVER floored to 1. Flooring drew a sub-1 column (a 0.51 cost range) with its max at ~51% of the
  // track instead of the right edge — the right half of the slider was simply unreachable — and pinned
  // the derived step at 1 so the handle only ever jumped between the extremes.
  const span = max - min || 1;
  // Never below what a write can survive. A weeks-based window rounds day counts back to whole weeks, so
  // a one-day step read straight back as the value it started from and the arrow keys moved nothing at
  // all — the handle is re-derived from the predicate each render, so the step never accumulated.
  const stepSize = Math.max(step && step > 0 ? step : defaultDragStep(span, integerValued), domain.granularity ?? 0);
  // ONE axis object for the forward map, its inverse, the keyboard step and the drag snap. Before
  // ACT2-569 these were four independent expressions in this file; on a warped axis they must agree by
  // construction, or the handle lands somewhere other than where the pointer released.
  //
  // The fifth argument is the WRITE granularity, and it is deliberately NOT `stepSize`. `stepSize` is
  // ~1/100th of the whole span (`defaultDragStep`), which on a log axis is a huge additive jump at the
  // low end and a rounding error at the high end — flooring the log step with it would reinstate the
  // linear grid. What a log step does have to respect is the smallest change a WRITE survives: a
  // weeks-based window rounds back to whole weeks, and an integer column must not be handed 8.4.
  const axis = axisScale(scale, min, max, stepSize, Math.max(domain.granularity ?? 0, integerValued ? 1 : 0));
  const op = domain.normalizeOp(operator);
  const isRange = RANGE_OPS.has(op);

  // Keep a value inside the plotted extent. No snapping: an ALREADY-COMMITTED value is a fact about the
  // rule, and the handle has to sit on it. Snapping it here made the chart contradict the number input
  // beside it — and, because the same number drives the readout, the count contradicted it too.
  const clampValue = (v: number) => Math.min(max, Math.max(min, v));
  // Snap a value the USER is placing (drag / arrow keys) onto the step grid — it keeps a drag landing on
  // round numbers. min/max stay exactly reachable even when they are not step multiples.
  const snapValue = (v: number) => {
    if (v <= min) return min;
    if (v >= max) return max;
    // On a log axis a fixed step grid is meaningless (the same 1,000 is a rounding error at 400,000 and
    // eight whole bins at 125), so the axis snaps to the value's own magnitude instead.
    return clampValue(scale === 'linear' ? snapToStep(v, stepSize) : axis.snap(v));
  };
  const pct = axis.pct;

  const { single: num, lo: rLo, hi: rHi } = domain.read(value);
  const single = clampValue(num ?? fallback.single ?? axis.valueAt(0.5));
  const lo = clampValue(rLo ?? fallback.lo);
  const hi = clampValue(rHi ?? fallback.hi);
  const committed = isRange ? rLo != null && rHi != null : num != null;

  const commitSingle = (v: number) => onPatch(domain.writeSingle(snapValue(v)));
  // Clamp the dragged handle against the stationary one so a bound can't cross and drag its partner along.
  //
  // The stationary bound is written back AS STORED, not as displayed. `lo`/`hi` above are clamped into
  // the plotted frame purely so the handles have somewhere to sit, and the frame is only p01/p99 — so a
  // legitimately out-of-frame bound (typed by hand, or written by the agent) displays at the edge.
  // Writing that edge value back destroyed the real one: moving the LOWER handle of `btw [10, 100000]`
  // on a chart framed to 5000 rewrote the upper bound to 5000. Same principle as the comment above —
  // a committed value is a fact about the rule, and nothing but the handle being moved may change it.
  const commitRange = (which: 0 | 1, v: number) => {
    const nv = snapValue(v);
    const heldLo = rLo ?? lo;
    const heldHi = rHi ?? hi;
    const [a, b] = which === 0 ? [Math.min(nv, heldHi), heldHi] : [heldLo, Math.max(nv, heldLo)];
    onPatch(domain.writeRange(a, b));
  };

  const valueFromClientX = (clientX: number) => {
    // Use the drag-cached rect while dragging; fall back to a fresh read for the initial pointer-down.
    const rect = dragRect.current ?? wrapRef.current?.getBoundingClientRect();
    if (!rect) return single;
    return clampValue(axis.valueAt((clientX - rect.left) / rect.width));
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    const el = wrapRef.current;
    el?.setPointerCapture(e.pointerId);
    dragRect.current = el?.getBoundingClientRect() ?? null;
    const v = valueFromClientX(e.clientX);
    if (isRange) {
      // Grab the handle on the side the pointer is pressing (nearest when between them). On an exact tie —
      // including a collapsed lo===hi point — take the UPPER handle so dragging right re-widens the range
      // (grabbing the lower one would pin it under `hi` and drag-right couldn't widen).
      //
      // "Nearest" is measured in SCREEN space, not value space — the sentence above is a statement about
      // where the pointer is, and on a log axis the two disagree wildly. Pressing at 500 on a [10, 1000]
      // frame is 85% along the track, right beside the upper handle, while `|500-10| < |500-1000|` hands
      // back the LOWER one: the user presses next to one handle and drags the other. `axis.pct` is affine
      // under `linear`, so this is the same expression as before on every linear chart. This is the one
      // place in the control that had not been routed through the axis pair.
      const which: 0 | 1 = v < lo ? 0 : v > hi ? 1 : Math.abs(pct(v) - pct(lo)) < Math.abs(pct(v) - pct(hi)) ? 0 : 1;
      setDragging(which);
      commitRange(which, v);
    } else {
      setDragging(0);
      commitSingle(v);
    }
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (dragging === null) return;
    const v = valueFromClientX(e.clientX);
    // Coalesce moves to one commit per frame — a raw pointermove can fire several times per frame, and each
    // commit re-renders the whole predicate tree. Keyboard + the initial pointer-down still commit at once.
    pendingRef.current = () => (isRange ? commitRange(dragging, v) : commitSingle(v));
    rafRef.current ??= requestAnimationFrame(flushDrag);
  };
  const endDrag = () => {
    // Flush the last pending move so the final position commits even if it landed mid-frame.
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pendingRef.current?.();
    pendingRef.current = null;
    dragRect.current = null;
    setDragging(null);
  };

  const handles: SliderHandle[] = isRange
    ? [
        {
          pos: pct(lo),
          value: lo,
          label: domain.format(lo),
          ariaLabel: `${ariaLabel} lower bound`,
          onKey: (v) => commitRange(0, v),
        },
        {
          pos: pct(hi),
          value: hi,
          label: domain.format(hi),
          ariaLabel: `${ariaLabel} upper bound`,
          onKey: (v) => commitRange(1, v),
        },
      ]
    : [{ pos: pct(single), value: single, label: domain.format(single), ariaLabel, onKey: commitSingle }];

  return {
    wrapRef,
    min,
    max,
    step: stepSize,
    op,
    isRange,
    committed,
    single,
    lo,
    hi,
    raw: { single: num, lo: rLo, hi: rHi },
    pct,
    stepAt: axis.stepAt,
    clampValue,
    handles,
    segments: continuousSegments(op, { single, lo, hi }, pct),
    disabled,
    pointerProps: { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag },
  };
}
